import { useState, useEffect, useMemo, type FormEvent } from 'react';
import { api, ApiRequestError } from '../lib/api';
import { useActiveJobs, type ActiveJob } from '../hooks/useActiveJobs';

interface Scenario {
  id: string;
  display_name: string;
  description: string;
}

const IMAGE_SIZES = [
  { value: '1024x1024', label: '1024×1024 (Square)', multiplier: 1 },
  { value: '1536x1024', label: '1536×1024 (Landscape)', multiplier: 1.5 },
  { value: '1024x1536', label: '1024×1536 (Portrait)', multiplier: 1.5 },
];

const QUALITY_LEVELS = [
  { value: 'low', label: 'Low', multiplier: 1 },
  { value: 'medium', label: 'Medium', multiplier: 2 },
  { value: 'high', label: 'High', multiplier: 3 },
];

const BASE_COST = 1;

function calculateCost(quality: string, size: string, variants: number): number {
  const qualityMul = QUALITY_LEVELS.find((q) => q.value === quality)?.multiplier ?? 1;
  const sizeMul = IMAGE_SIZES.find((s) => s.value === size)?.multiplier ?? 1;
  return Math.ceil(BASE_COST * qualityMul * sizeMul * variants);
}

function JobStatusBadge({ status }: { status: ActiveJob['status'] }) {
  const styles: Record<string, string> = {
    queued: 'bg-yellow-50 text-yellow-700',
    processing: 'bg-blue-50 text-blue-700',
    completed: 'bg-green-50 text-green-700',
    retrying: 'bg-orange-50 text-orange-700',
    permanently_failed: 'bg-red-50 text-red-700',
    failed: 'bg-red-50 text-red-700',
  };
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] ?? 'bg-gray-50 text-gray-700'}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

export function Workspace() {
  const [prompt, setPrompt] = useState('');
  const [imageSize, setImageSize] = useState('1024x1024');
  const [qualityLevel, setQualityLevel] = useState('medium');
  const [numVariants, setNumVariants] = useState(1);
  const [scenarioId, setScenarioId] = useState('');
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { jobs, refetch } = useActiveJobs();

  const creditCost = useMemo(
    () => calculateCost(qualityLevel, imageSize, numVariants),
    [qualityLevel, imageSize, numVariants],
  );

  useEffect(() => {
    api
      .get<{ scenarios: Scenario[] }>('/scenarios')
      .then((res) => setScenarios(res.data?.scenarios ?? []))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      await api.post('/generations', {
        prompt,
        image_size: imageSize,
        quality_level: qualityLevel,
        num_variants: numVariants,
        scenario_id: scenarioId || undefined,
      });
      setSuccess('Generation submitted! Check the queue on the right \u2192');
      setPrompt('');
      // Immediately refetch to show the new queued job
      setTimeout(() => refetch(), 500);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.error.message);
      } else {
        setError('Failed to submit generation');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Workspace</h1>
        <p className="text-gray-500 mt-1">Generate AI-powered images</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Generation Form */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}
            {success && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                {success}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Prompt */}
              <div>
                <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Prompt
                </label>
                <textarea
                  id="prompt"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  required
                  maxLength={4000}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y text-sm transition-shadow"
                  placeholder="Describe the image you want to generate..."
                />
                <div className="mt-1 text-xs text-gray-400 text-right">
                  {prompt.length}/4000
                </div>
              </div>

              {/* Image Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Image Size</label>
                <div className="flex flex-wrap gap-3">
                  {IMAGE_SIZES.map((size) => (
                    <label
                      key={size.value}
                      className={`flex items-center px-4 py-2.5 rounded-lg border cursor-pointer min-h-[44px] transition-all ${
                        imageSize === size.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-500'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="imageSize"
                        value={size.value}
                        checked={imageSize === size.value}
                        onChange={(e) => setImageSize(e.target.value)}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">{size.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Quality Level */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Quality Level</label>
                <div className="flex gap-3">
                  {QUALITY_LEVELS.map((q) => (
                    <label
                      key={q.value}
                      className={`flex items-center px-5 py-2.5 rounded-lg border cursor-pointer min-h-[44px] transition-all ${
                        qualityLevel === q.value
                          ? 'border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-500'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="qualityLevel"
                        value={q.value}
                        checked={qualityLevel === q.value}
                        onChange={(e) => setQualityLevel(e.target.value)}
                        className="sr-only"
                      />
                      <span className="text-sm font-medium">{q.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Variants */}
              <div>
                <label htmlFor="variants" className="block text-sm font-medium text-gray-700 mb-1.5">
                  Number of Variants
                </label>
                <select
                  id="variants"
                  value={numVariants}
                  onChange={(e) => setNumVariants(Number(e.target.value))}
                  className="w-full max-w-[140px] px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px] text-sm"
                >
                  {[1, 2, 3, 4].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>

              {/* Scenario */}
              {scenarios.length > 0 && (
                <div>
                  <label htmlFor="scenario" className="block text-sm font-medium text-gray-700 mb-1.5">
                    Scenario (optional)
                  </label>
                  <select
                    id="scenario"
                    value={scenarioId}
                    onChange={(e) => setScenarioId(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[44px] text-sm"
                  >
                    <option value="">None</option>
                    {scenarios.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.display_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Cost Preview + Submit */}
              <div className="flex items-center justify-between pt-5 border-t border-gray-100">
                <div className="text-sm text-gray-600">
                  Estimated cost:{' '}
                  <span className="font-semibold text-gray-900">{creditCost} credits</span>
                </div>
                <button
                  type="submit"
                  disabled={submitting || !prompt.trim()}
                  className="px-6 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] transition-all shadow-lg shadow-blue-500/25"
                >
                  {submitting ? 'Submitting...' : 'Generate'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Panel - Job Status */}
        <div className="space-y-4">
          {/* Queued Jobs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center justify-between">
              <span>{'\u23F3'} Queued</span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-600">
                {jobs.filter(j => j.status === 'queued').length}
              </span>
            </h2>
            {jobs.filter(j => j.status === 'queued').length === 0 ? (
              <p className="text-xs text-gray-400">No jobs in queue</p>
            ) : (
              <div className="space-y-2">
                {jobs.filter(j => j.status === 'queued').map((job) => (
                  <div key={job.id} className="p-2.5 bg-yellow-50 rounded-lg border border-yellow-100">
                    <p className="text-xs text-gray-700 line-clamp-1">{job.prompt}</p>
                    <p className="text-xs text-gray-400 mt-1">{new Date(job.created_at).toLocaleTimeString()}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Processing Jobs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center justify-between">
              <span>{'\u26A1'} Processing</span>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
                {jobs.filter(j => j.status === 'processing').length}
              </span>
            </h2>
            {jobs.filter(j => j.status === 'processing').length === 0 ? (
              <p className="text-xs text-gray-400">No jobs processing</p>
            ) : (
              <div className="space-y-2">
                {jobs.filter(j => j.status === 'processing').map((job) => (
                  <div key={job.id} className="p-2.5 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-xs text-gray-700 line-clamp-1">{job.prompt}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex-1 h-1 bg-blue-200 rounded-full overflow-hidden">
                        <div className="h-1 bg-blue-500 rounded-full animate-pulse w-2/3"></div>
                      </div>
                      <span className="text-xs text-blue-500">generating...</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Retrying Jobs */}
          {jobs.filter(j => j.status === 'retrying').length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h2 className="text-sm font-semibold text-gray-900 mb-3 flex items-center justify-between">
                <span>{'\uD83D\uDD04'} Retrying</span>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-50 text-orange-600">
                  {jobs.filter(j => j.status === 'retrying').length}
                </span>
              </h2>
              <div className="space-y-2">
                {jobs.filter(j => j.status === 'retrying').map((job) => (
                  <div key={job.id} className="p-2.5 bg-orange-50 rounded-lg border border-orange-100">
                    <p className="text-xs text-gray-700 line-clamp-1">{job.prompt}</p>
                    <p className="text-xs text-orange-500 mt-1">Retrying...</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Generations - shows ALL jobs with status */}
      {jobs.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Generations</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Prompt</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Size</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-500">Quality</th>
                  <th className="text-center py-2 px-3 text-xs font-medium text-gray-500">Status</th>
                  <th className="text-right py-2 px-3 text-xs font-medium text-gray-500">Time</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {jobs.slice(0, 20).map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-3 max-w-[300px]">
                      <p className="text-gray-700 truncate">{job.prompt}</p>
                      {job.error_message && (
                        <p className="text-xs text-red-500 truncate mt-0.5">{job.error_message}</p>
                      )}
                    </td>
                    <td className="py-3 px-3 text-gray-500 text-xs whitespace-nowrap">{job.image_size}</td>
                    <td className="py-3 px-3 text-gray-500 text-xs capitalize">{job.quality_level}</td>
                    <td className="py-3 px-3 text-center">
                      <JobStatusBadge status={job.status} />
                    </td>
                    <td className="py-3 px-3 text-gray-400 text-xs text-right whitespace-nowrap">
                      {new Date(job.created_at).toLocaleTimeString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
